import React, { useEffect, useState } from 'react';
import api from '../api';

// Rendert die im Admin konfigurierte Feldliste einer Kachel (Reihenfolge, Beschriftung, Pflicht, Breite).
// Werte werden nach Feld-ID gehalten. Systemfelder gehen beim Absenden in feste Spalten,
// Zusatzfelder unter ihrer Beschriftung ins JSON „zusatzfelder".

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const istEingabe = (f) => f.typ !== 'ueberschrift';
const heute = () => new Date().toISOString().split('T')[0];
const optionenVon = (f) => (f.optionen || []).map(o => (typeof o === 'string' ? { wert: o, label: o } : o));

export function useKachelFormular(key) {
  const [zustand, setZustand] = useState({ laden: true, fehler: false, kachel: null });

  useEffect(() => {
    let aktiv = true;
    setZustand({ laden: true, fehler: false, kachel: null });
    api.get(`/api/kacheln/${key}`)
      .then(r => { if (aktiv) setZustand({ laden: false, fehler: false, kachel: r.data }); })
      .catch(() => { if (aktiv) setZustand({ laden: false, fehler: true, kachel: null }); });
    return () => { aktiv = false; };
  }, [key]);

  return zustand;
}

export function validiereFelder(felder, werte) {
  const fehler = {};
  felder.forEach(f => {
    if (!istEingabe(f)) return;
    const w = werte[f.id];
    if (f.typ === 'boolean') {
      if (f.pflicht && !w) fehler[f.id] = 'Bitte bestätigen';
      return;
    }
    if (w === undefined || w === null || String(w).trim() === '') {
      if (f.pflicht) fehler[f.id] = (f.typ === 'select' || f.typ === 'radio') ? 'Bitte wählen' : 'Pflichtfeld';
      return;
    }
    if (f.typ === 'email' && !EMAIL_RE.test(String(w).trim())) fehler[f.id] = 'Gültige E-Mail erforderlich';
    if (f.typ === 'number') {
      const n = Number(w);
      if (!Number.isFinite(n) || (f.min != null && n < f.min) || (f.max != null && n > f.max))
        fehler[f.id] = 'Bitte eine gültige Zahl eingeben';
    }
    if (f.typ === 'date' && f.max === 'heute' && String(w) > heute()) fehler[f.id] = 'Datum liegt in der Zukunft';
  });
  return fehler;
}

// Teilt die Werte in { system: {spalte: wert}, zusatz: {beschriftung: wert} }
export function trenneWerte(felder, werte) {
  const system = {};
  const zusatz = {};
  felder.forEach(f => {
    if (!istEingabe(f)) return;
    let w = werte[f.id];
    if (f.typ === 'boolean') w = Boolean(w);
    else if (w === undefined || w === null) w = '';
    else if (typeof w === 'string') w = w.trim();
    if (f.system) system[f.speicher || f.id] = w;
    else zusatz[f.label] = w;
  });
  return { system, zusatz };
}

function Beschriftung({ f, htmlFor }) {
  return (
    <label className={`form-label${f.pflicht ? ' required' : ''}`} htmlFor={htmlFor}>
      {f.label}
      {f.hinweis
        ? <span className="form-label-optional"> ({f.hinweis})</span>
        : !f.pflicht && <span className="form-label-optional"> (optional)</span>}
    </label>
  );
}

function Feld({ f, wert, fehler, setze }) {
  const htmlId = `feld_${f.id}`;

  if (f.typ === 'ueberschrift') return <p className="form-section-title">{f.label}</p>;

  if (f.typ === 'boolean') {
    return (
      <div className="form-group">
        <label htmlFor={htmlId} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: '0.95rem' }}>
          <input id={htmlId} type="checkbox" checked={!!wert}
            onChange={e => setze(f.id, e.target.checked)}
            style={{ width: 18, height: 18, flexShrink: 0, marginTop: 2 }} />
          <span>
            {f.label}
            {f.pflicht && <span style={{ color: 'var(--accent)' }}> *</span>}
            {f.hinweis && <span className="form-label-optional"> ({f.hinweis})</span>}
          </span>
        </label>
        {fehler && <span className="form-error">{fehler}</span>}
      </div>
    );
  }

  let eingabe;
  const wertText = wert ?? '';

  if (f.typ === 'textarea') {
    eingabe = (
      <textarea id={htmlId} value={wertText} onChange={e => setze(f.id, e.target.value)}
        className={`form-textarea${fehler ? ' error' : ''}`} rows={f.zeilen || 3}
        placeholder={f.platzhalter || undefined} />
    );
  } else if (f.typ === 'select') {
    eingabe = (
      <select id={htmlId} value={wertText} onChange={e => setze(f.id, e.target.value)}
        className={`form-select${fehler ? ' error' : ''}`}>
        <option value="">-- Bitte wählen --</option>
        {optionenVon(f).map(o => <option key={o.wert} value={o.wert}>{o.label}</option>)}
      </select>
    );
  } else if (f.typ === 'radio') {
    eingabe = (
      <div className="radio-group" role="radiogroup" id={htmlId}>
        {optionenVon(f).map(o => (
          <label key={o.wert} className="radio-label">
            <input type="radio" name={htmlId} value={o.wert}
              checked={wertText === o.wert} onChange={() => setze(f.id, o.wert)} />
            {o.label}
          </label>
        ))}
      </div>
    );
  } else {
    const typ = ['email', 'tel', 'number', 'date'].includes(f.typ) ? f.typ : 'text';
    eingabe = (
      <input id={htmlId} type={typ} value={wertText} onChange={e => setze(f.id, e.target.value)}
        className={`form-input${fehler ? ' error' : ''}`}
        placeholder={f.platzhalter || undefined}
        autoComplete={f.autocomplete || undefined}
        min={f.min ?? undefined}
        max={f.max === 'heute' ? heute() : (f.max ?? undefined)} />
    );
  }

  return (
    <div className="form-group">
      <Beschriftung f={f} htmlFor={f.typ === 'radio' ? undefined : htmlId} />
      {eingabe}
      {fehler && <span className="form-error">{fehler}</span>}
    </div>
  );
}

// nachFeld(feld) darf zusätzlichen Inhalt direkt hinter einem Feld (bzw. seiner Zeile) liefern.
export default function FormularFelder({ felder, werte, fehler, onChange, nachFeld }) {
  const sichtbar = felder.filter(f => !f.ausgeblendet);

  // Aufeinanderfolgende Felder mit halber Breite paarweise nebeneinander
  const bloecke = [];
  let offen = null;
  sichtbar.forEach(f => {
    if (istEingabe(f) && f.breite === 'halb') {
      if (offen) { offen.felder.push(f); offen = null; }
      else { offen = { halb: true, felder: [f] }; bloecke.push(offen); }
    } else {
      offen = null;
      bloecke.push({ halb: false, felder: [f] });
    }
  });

  const zeige = (f) => (
    <Feld key={f.id} f={f} wert={werte[f.id]} fehler={fehler[f.id]} setze={onChange} />
  );

  return (
    <>
      {bloecke.map(b => (
        <React.Fragment key={b.felder[0].id}>
          {b.halb ? <div className="form-row">{b.felder.map(zeige)}</div> : zeige(b.felder[0])}
          {nachFeld && b.felder.map(f => <React.Fragment key={`nach_${f.id}`}>{nachFeld(f)}</React.Fragment>)}
        </React.Fragment>
      ))}
    </>
  );
}
